import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchFeaturedProducts, fetchNewProducts, fetchProducts } from '@/lib/api';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { useProducts } from '@/contexts/ProductContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import { RefreshCw } from 'lucide-react';

const Dashboard = () => {
  const { refreshProducts, isSyncing, lastSyncTime } = useProducts();

  // Buscar dados dos produtos
  const { data: allProducts = [] } = useQuery({
    queryKey: ['products'],
    queryFn: async () => fetchProducts({}),
  });

  const { data: featuredProducts = [] } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: async () => fetchFeaturedProducts(),
  });

  const { data: newProducts = [] } = useQuery({
    queryKey: ['products', 'new'],
    queryFn: async () => fetchNewProducts(),
  });

  // Criar dados para o gráfico
  const chartData = [
    {
      name: 'Total',
      total: allProducts.length,
    },
    {
      name: 'Destaque',
      total: featuredProducts.length,
    },
    {
      name: 'Novos',
      total: newProducts.length,
    },
  ];

  // Formatar a data da última sincronização
  const formatLastSync = () => {
    if (!lastSyncTime) return 'Nunca';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(lastSyncTime);
  };

  return (
    <DashboardLayout title="Dashboard">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Produtos</CardTitle>
            <div className="h-4 w-4 rounded-full bg-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{allProducts.length}</div>
            <p className="text-xs text-muted-foreground">
              produtos cadastrados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Produtos em Destaque</CardTitle>
            <div className="h-4 w-4 rounded-full bg-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{featuredProducts.length}</div>
            <p className="text-xs text-muted-foreground">
              {((featuredProducts.length / allProducts.length) * 100 || 0).toFixed(1)}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Novos Produtos</CardTitle>
            <div className="h-4 w-4 rounded-full bg-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{newProducts.length}</div>
            <p className="text-xs text-muted-foreground">
              {((newProducts.length / allProducts.length) * 100 || 0).toFixed(1)}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Sincronização</CardTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => refreshProducts()} 
              disabled={isSyncing}
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatLastSync()}</div>
            <p className="text-xs text-muted-foreground">
              {isSyncing ? 'Sincronizando...' : 'Clique no ícone para atualizar'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Visão Geral dos Produtos</CardTitle>
            <CardDescription>
              Distribuição de produtos por categoria
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis
                    dataKey="name"
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip />
                  <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard; 